export type GpsCoordinate = {
    latitude: number;
    longitude: number;
};

const earthRadiusMeters = 6_371_000;
const boundaryEpsilon = 1e-12;

export function pointInPolygon(point: GpsCoordinate, vertices: readonly GpsCoordinate[]): boolean {
    if (vertices.length < 3) {
        return false;
    }

    let inside = false;
    for (let index = 0, previousIndex = vertices.length - 1; index < vertices.length; previousIndex = index++) {
        const current = vertices[index];
        const previous = vertices[previousIndex];
        if (pointOnSegment(point, previous, current)) {
            return true;
        }

        const crossesLatitude = (current.latitude > point.latitude) !== (previous.latitude > point.latitude);
        if (!crossesLatitude) {
            continue;
        }

        const crossingLongitude = previous.longitude +
            (point.latitude - previous.latitude) * (current.longitude - previous.longitude) /
            (current.latitude - previous.latitude);
        if (point.longitude < crossingLongitude) {
            inside = !inside;
        }
    }

    return inside;
}

export function distanceToPolygonMeters(point: GpsCoordinate, vertices: readonly GpsCoordinate[]): number {
    if (vertices.length < 3) {
        return Number.POSITIVE_INFINITY;
    }
    if (pointInPolygon(point, vertices)) {
        return 0;
    }

    let shortest = Number.POSITIVE_INFINITY;
    for (let index = 0; index < vertices.length; index++) {
        const start = toLocalMetres(point, vertices[index]);
        const end = toLocalMetres(point, vertices[(index + 1) % vertices.length]);
        shortest = Math.min(shortest, distanceFromOriginToSegment(start, end));
    }

    return shortest;
}

function pointOnSegment(point: GpsCoordinate, start: GpsCoordinate, end: GpsCoordinate): boolean {
    const latitudeSpan = end.latitude - start.latitude;
    const longitudeSpan = end.longitude - start.longitude;
    const cross = (point.latitude - start.latitude) * longitudeSpan -
        (point.longitude - start.longitude) * latitudeSpan;
    const scale = Math.max(1, Math.abs(latitudeSpan), Math.abs(longitudeSpan));
    if (Math.abs(cross) > boundaryEpsilon * scale) {
        return false;
    }

    const dot = (point.latitude - start.latitude) * (point.latitude - end.latitude) +
        (point.longitude - start.longitude) * (point.longitude - end.longitude);
    return dot <= boundaryEpsilon;
}

function toLocalMetres(origin: GpsCoordinate, coordinate: GpsCoordinate): { x: number; y: number } {
    const meanLatitudeRadians = toRadians((origin.latitude + coordinate.latitude) / 2);
    return {
        x: toRadians(coordinate.longitude - origin.longitude) * earthRadiusMeters * Math.cos(meanLatitudeRadians),
        y: toRadians(coordinate.latitude - origin.latitude) * earthRadiusMeters
    };
}

function distanceFromOriginToSegment(start: { x: number; y: number }, end: { x: number; y: number }): number {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSquared = dx * dx + dy * dy;
    if (lengthSquared === 0) {
        return Math.hypot(start.x, start.y);
    }

    const projection = Math.max(0, Math.min(1, -(start.x * dx + start.y * dy) / lengthSquared));
    return Math.hypot(start.x + projection * dx, start.y + projection * dy);
}

function toRadians(value: number): number {
    return value * Math.PI / 180;
}
